<script setup lang="ts">
import { AlertTriangle, ArrowRight, Check, Clipboard, KeyRound, LoaderCircle, Play, RotateCcw } from "lucide-vue-next"
import { computed, ref, watch } from "vue"

import AppSelect from "../components/AppSelect.vue"
import { ApiError, apiRequest } from "../lib/api"
import type { ExecutionResponse } from "../lib/types"
import { useApiKeysStore } from "../stores/apiKeys"
import { useSessionStore } from "../stores/session"

type Platform = "rednote" | "zhihu" | "tophub" | "douban" | "douyin" | "weibo" | "weibo_web" | "smzdm" | "bilibili" | "v2ex" | "xueqiu" | "reddit" | "twitter" | "xianyu" | "jd" | "1688" | "nowcoder" | "instagram" | "maimai" | "jianyu" | "toutiao" | "sinafinance" | "juejin" | "36kr" | "dongchedi" | "ctrip" | "linkedin" | "github" | "google" | "imdb"
type OperationKind = "search" | "trending" | "detail" | "comments" | "navigation" | "items"
type OperationId =
  | "rednote.search_notes"
  | "rednote.trending"
  | "rednote.note_detail"
  | "rednote.note_comments"
  | "rednote.local_feed"
  | "rednote.local_feed_header"
  | "rednote.user_posts"
  | "zhihu.search"
  | "zhihu.hot"
  | "zhihu.article"
  | "zhihu.answer"
  | "zhihu.comments"
  | "tophub.categories"
  | "tophub.tags"
  | "tophub.items"
  | "douban.search"
  | "douban.subject"
  | "douban.reviews"
  | "douban.comments"
  | "weibo.search"
  | "weibo.hot"
  | "weibo.status"
  | "weibo.comments"
  | "weibo_web.search"
  | "weibo_web.hot"
  | "weibo_web.status"
  | "weibo_web.comments"
  | "smzdm.search"
  | "smzdm.hot"
  | "smzdm.content"
  | "smzdm.detail"
  | "smzdm.comments"
  | "bilibili.search"
  | "bilibili.video"
  | "bilibili.hot"
  | "bilibili.rank"
  | "bilibili.comments"
  | "douyin.search"
  | "douyin.detail"
  | "douyin.comments"
  | "douyin.hot"
  | "douyin.user_posts"
  | "v2ex.hot"
  | "v2ex.latest"
  | "v2ex.node_topics"
  | "v2ex.topic"
  | "v2ex.replies"
  | "xueqiu.search_stocks"
  | "xueqiu.quote"
  | "xueqiu.hot_posts"
  | "xueqiu.hot_stocks"
  | "reddit.search"
  | "reddit.popular"
  | "reddit.subreddit"
  | "reddit.detail"
  | "twitter.search"
  | "twitter.tweet"
  | "twitter.article"
  | "jd.search"
  | "jd.item"
  | "xianyu.search"
  | "xianyu.item"
  | "1688.item"
  | "1688.store_summary"
  | "nowcoder.search"
  | "nowcoder.detail"
  | "nowcoder.papers"
  | "nowcoder.experience"
  | "instagram.search_users"
  | "instagram.profile"
  | "instagram.user_posts"
  | "instagram.post"
  | "maimai.search_posts"
  | "maimai.search_jobs"
  | "maimai.search_companies"
  | "maimai.search_talents"
  | "maimai.post"
  | "maimai.comments"
  | "jianyu.search"
  | "jianyu.detail"
  | "toutiao.hot"
  | "toutiao.recommend"
  | "toutiao.search"
  | "toutiao.detail"
  | "sinafinance.live_news"
  | "sinafinance.rolling_news"
  | "sinafinance.search"
  | "sinafinance.detail"
  | "sinafinance.quote"
  | "sinafinance.stock_rank"
  | "juejin.search"
  | "juejin.detail"
  | "juejin.hot"
  | "juejin.recommend"
  | "36kr.news"
  | "36kr.search"
  | "36kr.article"
  | "36kr.hot"
  | "dongchedi.search"
  | "dongchedi.series"
  | "dongchedi.models"
  | "dongchedi.specs"
  | "dongchedi.score"
  | "dongchedi.reviews"
  | "ctrip.search"
  | "ctrip.hotel_suggest"
  | "ctrip.hotels"
  | "ctrip.hotel_detail"
  | "ctrip.flights"
  | "ctrip.trains"
  | "ctrip.attractions"
  | "linkedin.search"
  | "linkedin.job_detail"
  | "linkedin.profile"
  | "linkedin.company"
  | "linkedin.posts"
  | "github.trending_repos"
  | "google.search"
  | "google.news"
  | "google.suggest"
  | "google.trends"
  | "imdb.search"
  | "imdb.title"
  | "imdb.top"
  | "imdb.trending"
  | "imdb.person"
  | "imdb.reviews"

interface OperationOption {
  id: OperationId
  platform: Platform
  label: string
  description: string
  kind: OperationKind
  price: number
  resourceLabel?: string
  resourcePlaceholder?: string
  disabled?: boolean
}

const platformLabels: Record<Platform, string> = {
  rednote: "RedNote",
  zhihu: "Zhihu",
  tophub: "TopHub",
  douban: "Douban",
  weibo: "Weibo CLI",
  weibo_web: "Weibo Web",
  smzdm: "SMZDM",
  bilibili: "Bilibili",
  douyin: "Douyin",
  v2ex: "V2EX",
  xueqiu: "Xueqiu",
  reddit: "Reddit",
  twitter: "Twitter / X",
  jd: "JD",
  xianyu: "闲鱼",
  "1688": "1688",
  nowcoder: "Nowcoder",
  instagram: "Instagram",
  maimai: "Maimai",
  jianyu: "剑鱼标讯",
  toutiao: "Toutiao",
  sinafinance: "Sina Finance",
  juejin: "Juejin",
  "36kr": "36Kr",
  dongchedi: "Dongchedi",
  ctrip: "Ctrip",
  linkedin: "LinkedIn",
  github: "GitHub",
  google: "Google",
  imdb: "IMDb",
}
const platformOptions = Object.entries(platformLabels).map(([id, label]) => ({ id: id as Platform, label }))

const operations: OperationOption[] = [
  { id: "rednote.search_notes", platform: "rednote", label: "搜索笔记", description: "按关键词搜索公开笔记", kind: "search", price: 2 },
  { id: "rednote.trending", platform: "rednote", label: "趋势词", description: "获取公开搜索趋势", kind: "trending", price: 2 },
  { id: "rednote.note_detail", platform: "rednote", label: "笔记详情", description: "按笔记 ID 获取详情", kind: "detail", price: 1, resourceLabel: "笔记 ID", resourcePlaceholder: "note_id" },
  { id: "rednote.note_comments", platform: "rednote", label: "笔记评论", description: "获取笔记公开评论", kind: "comments", price: 2, resourceLabel: "笔记 ID", resourcePlaceholder: "note_id" },
  { id: "rednote.local_feed", platform: "rednote", label: "城市笔记", description: "按经纬度读取所在城市 Tab 的笔记", kind: "trending", price: 2 },
  { id: "rednote.local_feed_header", platform: "rednote", label: "城市解析", description: "查看经纬度解析到的城市", kind: "navigation", price: 1 },
  { id: "rednote.user_posts", platform: "rednote", label: "用户作品", description: "按公开用户 ID 读取主页作品", kind: "comments", price: 2, resourceLabel: "用户 ID", resourcePlaceholder: "搜索或笔记作者 creator.uid" },
  { id: "zhihu.search", platform: "zhihu", label: "搜索内容", description: "搜索公开回答与文章", kind: "search", price: 2 },
  { id: "zhihu.hot", platform: "zhihu", label: "热榜", description: "获取公开热榜内容", kind: "trending", price: 2 },
  { id: "zhihu.article", platform: "zhihu", label: "文章详情", description: "按文章 ID 获取详情", kind: "detail", price: 1, resourceLabel: "文章 ID", resourcePlaceholder: "article_id" },
  { id: "zhihu.answer", platform: "zhihu", label: "回答详情", description: "按回答 ID 获取详情", kind: "detail", price: 1, resourceLabel: "回答 ID", resourcePlaceholder: "answer_id" },
  { id: "zhihu.comments", platform: "zhihu", label: "内容评论", description: "获取回答或文章评论", kind: "comments", price: 2, resourceLabel: "内容 ID", resourcePlaceholder: "content_id" },
  { id: "tophub.categories", platform: "tophub", label: "主导航", description: "获取 TopHub 主导航与页面能力", kind: "navigation", price: 1 },
  { id: "tophub.tags", platform: "tophub", label: "分类关键词", description: "解析分类页二级导航关键词", kind: "navigation", price: 1 },
  { id: "tophub.items", platform: "tophub", label: "榜单资讯", description: "按分类、关键词和页码获取榜单资讯", kind: "items", price: 1 },
  { id: "douban.search", platform: "douban", label: "搜索条目", description: "按类型搜索豆瓣条目", kind: "search", price: 1 },
  { id: "douban.subject", platform: "douban", label: "条目详情", description: "读取电影、图书等条目详情", kind: "detail", price: 1, resourceLabel: "条目 ID", resourcePlaceholder: "subject_id" },
  { id: "douban.reviews", platform: "douban", label: "长评", description: "读取条目长评", kind: "comments", price: 1, resourceLabel: "条目 ID", resourcePlaceholder: "subject_id" },
  { id: "douban.comments", platform: "douban", label: "短评", description: "读取条目短评", kind: "comments", price: 1, resourceLabel: "条目 ID", resourcePlaceholder: "subject_id" },
  { id: "weibo.search", platform: "weibo", label: "搜索微博", description: "搜索公开微博", kind: "search", price: 1 },
  { id: "weibo.hot", platform: "weibo", label: "微博热搜", description: "读取公开热搜词", kind: "trending", price: 1 },
  { id: "weibo.status", platform: "weibo", label: "微博正文", description: "读取一条公开微博", kind: "detail", price: 1, resourceLabel: "微博 ID", resourcePlaceholder: "status_id" },
  { id: "weibo.comments", platform: "weibo", label: "微博评论", description: "读取微博公开评论", kind: "comments", price: 1, resourceLabel: "微博 ID", resourcePlaceholder: "status_id" },
  { id: "weibo_web.search", platform: "weibo_web", label: "网页搜索微博", description: "通过网页 HTTP 搜索公开微博", kind: "search", price: 1 },
  { id: "weibo_web.hot", platform: "weibo_web", label: "网页微博热搜", description: "匿名读取网页热搜词", kind: "trending", price: 1 },
  { id: "weibo_web.status", platform: "weibo_web", label: "网页微博正文", description: "通过网页 HTTP 读取微博正文", kind: "detail", price: 1, resourceLabel: "微博 ID", resourcePlaceholder: "status_id 或 mblogid" },
  { id: "weibo_web.comments", platform: "weibo_web", label: "网页微博评论", description: "通过网页 HTTP 读取公开评论", kind: "comments", price: 1, resourceLabel: "微博 ID", resourcePlaceholder: "status_id 或 mblogid" },
  { id: "smzdm.search", platform: "smzdm", label: "搜索内容", description: "搜索好价、百科与社区内容", kind: "search", price: 1 },
  { id: "smzdm.hot", platform: "smzdm", label: "搜索热词", description: "读取公开搜索热词", kind: "trending", price: 1 },
  { id: "smzdm.content", platform: "smzdm", label: "内容摘要", description: "读取好价或社区文章的标准字段", kind: "detail", price: 1, resourceLabel: "内容 ID", resourcePlaceholder: "article_id" },
  { id: "smzdm.detail", platform: "smzdm", label: "完整详情", description: "读取完整预加载 JSON", kind: "detail", price: 1, resourceLabel: "内容 ID", resourcePlaceholder: "article_id" },
  { id: "smzdm.comments", platform: "smzdm", label: "内容评论", description: "读取公开评论", kind: "comments", price: 1, resourceLabel: "内容 ID", resourcePlaceholder: "article_id" },
  { id: "bilibili.search", platform: "bilibili", label: "搜索视频", description: "按关键词搜索公开视频", kind: "search", price: 1 },
  { id: "bilibili.video", platform: "bilibili", label: "视频详情", description: "按 BV / AV 号读取视频详情", kind: "detail", price: 1, resourceLabel: "BV / AV 号", resourcePlaceholder: "BV1xx411c7mD" },
  { id: "bilibili.hot", platform: "bilibili", label: "热门视频", description: "读取全站热门视频", kind: "trending", price: 1 },
  { id: "bilibili.rank", platform: "bilibili", label: "全站排行", description: "读取全站视频排行榜", kind: "trending", price: 1 },
  { id: "bilibili.comments", platform: "bilibili", label: "视频评论", description: "按 BV / AV 号读取公开评论", kind: "comments", price: 1, resourceLabel: "BV / AV 号", resourcePlaceholder: "BV1xx411c7mD" },
  { id: "douyin.search", platform: "douyin", label: "搜索作品", description: "按关键词搜索抖音公开作品", kind: "search", price: 1 },
  { id: "douyin.detail", platform: "douyin", label: "作品详情", description: "按作品 ID 读取公开详情", kind: "detail", price: 1, resourceLabel: "作品 ID", resourcePlaceholder: "7372484719365098803" },
  { id: "douyin.comments", platform: "douyin", label: "作品评论", description: "按作品 ID 读取公开评论", kind: "comments", price: 1, resourceLabel: "作品 ID", resourcePlaceholder: "7372484719365098803" },
  { id: "douyin.hot", platform: "douyin", label: "抖音热榜", description: "读取当前公开热榜", kind: "trending", price: 1 },
  { id: "douyin.user_posts", platform: "douyin", label: "用户作品", description: "按 sec_uid 读取用户公开作品", kind: "comments", price: 1, resourceLabel: "用户 sec_uid", resourcePlaceholder: "MS4wLjABAAAA-example" },
  { id: "v2ex.hot", platform: "v2ex", label: "热门主题", description: "读取当前热门主题", kind: "trending", price: 1 },
  { id: "v2ex.latest", platform: "v2ex", label: "最新主题", description: "读取最新公开主题", kind: "trending", price: 1 },
  { id: "v2ex.node_topics", platform: "v2ex", label: "节点主题", description: "读取指定节点主题", kind: "comments", price: 1, resourceLabel: "节点名", resourcePlaceholder: "python" },
  { id: "v2ex.topic", platform: "v2ex", label: "主题详情", description: "读取主题正文", kind: "detail", price: 1, resourceLabel: "主题 ID", resourcePlaceholder: "1" },
  { id: "v2ex.replies", platform: "v2ex", label: "主题回复", description: "读取主题公开回复", kind: "comments", price: 1, resourceLabel: "主题 ID", resourcePlaceholder: "1" },
  { id: "xueqiu.search_stocks", platform: "xueqiu", label: "搜索股票", description: "按名称或代码搜索股票", kind: "search", price: 1 },
  { id: "xueqiu.quote", platform: "xueqiu", label: "股票行情", description: "读取股票公开行情", kind: "detail", price: 1, resourceLabel: "股票代码", resourcePlaceholder: "SH600519" },
  { id: "xueqiu.hot_posts", platform: "xueqiu", label: "热门讨论", description: "读取热门公开讨论", kind: "trending", price: 1 },
  { id: "xueqiu.hot_stocks", platform: "xueqiu", label: "热门股票", description: "读取热门股票", kind: "trending", price: 1 },
  { id: "reddit.search", platform: "reddit", label: "搜索帖子", description: "搜索公开 Reddit 帖子", kind: "search", price: 1 },
  { id: "reddit.popular", platform: "reddit", label: "热门帖子", description: "读取热门公开帖子", kind: "trending", price: 1 },
  { id: "reddit.subreddit", platform: "reddit", label: "社区帖子", description: "读取指定 subreddit", kind: "comments", price: 1, resourceLabel: "Subreddit", resourcePlaceholder: "technology" },
  { id: "reddit.detail", platform: "reddit", label: "帖子详情", description: "读取帖子及公开评论", kind: "comments", price: 1, resourceLabel: "帖子 ID", resourcePlaceholder: "abc123" },
  { id: "twitter.search", platform: "twitter", label: "搜索帖子", description: "搜索公开帖子", kind: "search", price: 1 },
  { id: "twitter.tweet", platform: "twitter", label: "帖子详情", description: "读取公开帖子与回复", kind: "comments", price: 1, resourceLabel: "Tweet ID", resourcePlaceholder: "1234567890" },
  { id: "twitter.article", platform: "twitter", label: "文章详情", description: "读取公开长文章", kind: "detail", price: 1, resourceLabel: "Tweet ID", resourcePlaceholder: "1234567890" },
  { id: "jd.search", platform: "jd", label: "搜索商品", description: "搜索公开商品", kind: "search", price: 1 },
  { id: "jd.item", platform: "jd", label: "商品详情", description: "按 SKU 读取公开商品与图文详情", kind: "detail", price: 1, resourceLabel: "SKU ID", resourcePlaceholder: "100291143898" },
  { id: "xianyu.search", platform: "xianyu", label: "搜索闲置商品", description: "搜索闲鱼公开二手商品", kind: "search", price: 1 },
  { id: "xianyu.item", platform: "xianyu", label: "闲置商品详情", description: "按商品 ID 读取公开闲鱼商品详情", kind: "detail", price: 1, resourceLabel: "商品 ID", resourcePlaceholder: "987654321012" },
  { id: "1688.item", platform: "1688", label: "商品详情", description: "按 Offer ID 读取公开批发商品详情", kind: "detail", price: 1, resourceLabel: "Offer ID", resourcePlaceholder: "887904326744" },
  { id: "1688.store_summary", platform: "1688", label: "店铺摘要", description: "按 Member ID 读取公开店铺摘要", kind: "detail", price: 1, resourceLabel: "Member ID", resourcePlaceholder: "b2b-1641351767" },
  { id: "nowcoder.search", platform: "nowcoder", label: "搜索内容", description: "搜索牛客公开帖子", kind: "search", price: 1 },
  { id: "nowcoder.detail", platform: "nowcoder", label: "内容详情", description: "读取牛客公开帖子或面经正文", kind: "detail", price: 1, resourceLabel: "内容 ID", resourcePlaceholder: "6aac24369f4c40638907a84a7654dcf4" },
  { id: "nowcoder.papers", platform: "nowcoder", label: "面试题库", description: "读取后端岗位公开面试题卷", kind: "trending", price: 1 },
  { id: "nowcoder.experience", platform: "nowcoder", label: "面试经验", description: "读取公开面经列表", kind: "trending", price: 1 },
  { id: "instagram.search_users", platform: "instagram", label: "搜索用户", description: "查找 Instagram 用户建议，不支持翻页", kind: "search", price: 1 },
  { id: "instagram.profile", platform: "instagram", label: "公开用户资料", description: "按准确用户名读取公开资料", kind: "detail", price: 1, resourceLabel: "用户名", resourcePlaceholder: "openai" },
  { id: "instagram.user_posts", platform: "instagram", label: "用户公开帖子", description: "按用户名读取公开帖子，每页至少 12 条", kind: "detail", price: 1, resourceLabel: "用户名", resourcePlaceholder: "openai" },
  { id: "instagram.post", platform: "instagram", label: "帖子详情", description: "按 shortcode 读取公开帖子", kind: "detail", price: 1, resourceLabel: "Shortcode", resourcePlaceholder: "C0Example" },
  { id: "maimai.search_posts", platform: "maimai", label: "搜索帖子", description: "按关键词搜索脉脉公开职场帖子", kind: "search", price: 1 },
  { id: "maimai.search_jobs", platform: "maimai", label: "搜索职位", description: "按关键词搜索脉脉职位", kind: "search", price: 1 },
  { id: "maimai.search_companies", platform: "maimai", label: "搜索公司", description: "按关键词搜索脉脉公司", kind: "search", price: 1 },
  { id: "maimai.search_talents", platform: "maimai", label: "人才匹配", description: "只返回匿名职业匹配信息，不输出个人资料", kind: "search", price: 1 },
  { id: "maimai.post", platform: "maimai", label: "帖子详情", description: "读取帖子完整正文，不输出作者资料", kind: "detail", price: 1, resourceLabel: "帖子 ID", resourcePlaceholder: "搜索结果 uid" },
  { id: "maimai.comments", platform: "maimai", label: "帖子评论", description: "读取公开评论，不输出评论者资料", kind: "comments", price: 1, resourceLabel: "帖子 ID", resourcePlaceholder: "搜索结果 uid" },
  { id: "jianyu.search", platform: "jianyu", label: "搜索采购公告", description: "搜索公开招投标与采购公告", kind: "search", price: 1 },
  { id: "jianyu.detail", platform: "jianyu", label: "采购公告详情", description: "通过 JSON 接口读取公告正文", kind: "detail", price: 1, resourceLabel: "公告 ID", resourcePlaceholder: "搜索结果 uid" },
  { id: "toutiao.hot", platform: "toutiao", label: "今日热榜", description: "读取今日头条公开热榜", kind: "trending", price: 1 },
  { id: "toutiao.recommend", platform: "toutiao", label: "分类推荐", description: "读取综合、科技、财经等公开推荐流", kind: "trending", price: 1 },
  { id: "toutiao.search", platform: "toutiao", label: "搜索内容", description: "搜索公开文章、视频与微头条", kind: "search", price: 1 },
  { id: "toutiao.detail", platform: "toutiao", label: "内容详情", description: "读取文章、视频或微头条完整内容", kind: "detail", price: 1, resourceLabel: "内容 ID", resourcePlaceholder: "7672662480929227299" },
  { id: "sinafinance.live_news", platform: "sinafinance", label: "7x24 快讯", description: "读取新浪财经实时快讯", kind: "trending", price: 1 },
  { id: "sinafinance.rolling_news", platform: "sinafinance", label: "滚动新闻", description: "读取新浪财经滚动资讯", kind: "trending", price: 1 },
  { id: "sinafinance.search", platform: "sinafinance", label: "搜索新闻", description: "搜索新浪财经公开新闻", kind: "search", price: 1 },
  { id: "sinafinance.detail", platform: "sinafinance", label: "新闻详情", description: "使用列表或搜索返回的定位符读取全文", kind: "detail", price: 1, resourceLabel: "详情定位符", resourcePlaceholder: "搜索结果 uid" },
  { id: "sinafinance.quote", platform: "sinafinance", label: "股票行情", description: "读取 A 股、港股或美股公开行情", kind: "detail", price: 1, resourceLabel: "股票代码", resourcePlaceholder: "600519,AAPL,00700" },
  { id: "sinafinance.stock_rank", platform: "sinafinance", label: "热门股票", description: "读取指定市场热门股票排行", kind: "trending", price: 1 },
  { id: "juejin.search", platform: "juejin", label: "搜索文章", description: "按关键词搜索掘金技术文章", kind: "search", price: 1 },
  { id: "juejin.detail", platform: "juejin", label: "文章详情", description: "读取掘金文章完整 Markdown 正文", kind: "detail", price: 1, resourceLabel: "文章 ID", resourcePlaceholder: "7352076505157877796" },
  { id: "juejin.hot", platform: "juejin", label: "分类热榜", description: "读取后端、前端、AI 等分类热榜", kind: "trending", price: 1 },
  { id: "juejin.recommend", platform: "juejin", label: "推荐文章", description: "读取掘金公开推荐流", kind: "trending", price: 1 },
  { id: "36kr.news", platform: "36kr", label: "最新资讯", description: "读取 36氪公开新闻源", kind: "trending", price: 1 },
  { id: "36kr.search", platform: "36kr", label: "搜索文章", description: "按关键词搜索 36氪文章", kind: "search", price: 1 },
  { id: "36kr.article", platform: "36kr", label: "文章详情", description: "读取 36氪文章完整正文", kind: "detail", price: 1, resourceLabel: "文章 ID", resourcePlaceholder: "3936164243914115" },
  { id: "36kr.hot", platform: "36kr", label: "热门文章", description: "读取人气、综合或收藏榜", kind: "trending", price: 1 },
  { id: "dongchedi.search", platform: "dongchedi", label: "搜索车系", description: "按品牌或车型关键词搜索车系", kind: "search", price: 1 },
  { id: "dongchedi.series", platform: "dongchedi", label: "车系详情", description: "读取车系基础信息", kind: "detail", price: 1, resourceLabel: "车系 ID", resourcePlaceholder: "5273" },
  { id: "dongchedi.models", platform: "dongchedi", label: "车型列表", description: "读取车系在售车型", kind: "items", price: 1, resourceLabel: "车系 ID", resourcePlaceholder: "5273" },
  { id: "dongchedi.specs", platform: "dongchedi", label: "参数配置", description: "读取车系车型参数配置", kind: "detail", price: 1, resourceLabel: "车系 ID", resourcePlaceholder: "5273" },
  { id: "dongchedi.score", platform: "dongchedi", label: "车系评分", description: "读取车系综合评分", kind: "detail", price: 1, resourceLabel: "车系 ID", resourcePlaceholder: "5273" },
  { id: "dongchedi.reviews", platform: "dongchedi", label: "车主口碑", description: "读取车主完整口碑正文", kind: "comments", price: 1, resourceLabel: "车系 ID", resourcePlaceholder: "5273" },
  { id: "ctrip.search", platform: "ctrip", label: "搜索旅行内容", description: "搜索携程攻略、酒店和景点", kind: "search", price: 1 },
  { id: "ctrip.hotel_suggest", platform: "ctrip", label: "酒店联想", description: "搜索酒店关键词与目的地", kind: "search", price: 1 },
  { id: "ctrip.hotels", platform: "ctrip", label: "搜索酒店", description: "搜索酒店公开起价、评分和位置（不含会员专享价，仅支持第一页）", kind: "trending", price: 1 },
  { id: "ctrip.hotel_detail", platform: "ctrip", label: "酒店详情", description: "读取酒店基本信息、点评和标签", kind: "detail", price: 1, resourceLabel: "酒店 ID", resourcePlaceholder: "51929837" },
  { id: "ctrip.flights", platform: "ctrip", label: "搜索机票", description: "搜索航班与票价，需要已完成短信登录的携程账号；触发验证时自动尝试处理", kind: "trending", price: 1 },
  { id: "ctrip.trains", platform: "ctrip", label: "搜索火车票（暂不可用）", description: "当前 App 接入不支持火车票", kind: "trending", price: 1, disabled: true },
  { id: "ctrip.attractions", platform: "ctrip", label: "搜索景点", description: "按目的地搜索景点", kind: "trending", price: 1 },
  { id: "linkedin.search", platform: "linkedin", label: "搜索职位", description: "使用登录账号搜索 LinkedIn 职位", kind: "search", price: 1 },
  { id: "linkedin.job_detail", platform: "linkedin", label: "职位详情", description: "按职位 ID 读取完整职位信息", kind: "detail", price: 1, resourceLabel: "职位 ID", resourcePlaceholder: "搜索结果中的 job_id" },
  { id: "linkedin.profile", platform: "linkedin", label: "职业资料", description: "读取可见的公开职业资料", kind: "detail", price: 1, resourceLabel: "Profile handle", resourcePlaceholder: "例如 satyanadella" },
  { id: "linkedin.company", platform: "linkedin", label: "公司详情", description: "读取可见的公司资料", kind: "detail", price: 1, resourceLabel: "Company slug", resourcePlaceholder: "例如 microsoft" },
  { id: "linkedin.posts", platform: "linkedin", label: "用户帖子", description: "读取职业资料页可见的帖子", kind: "items", price: 1, resourceLabel: "Profile handle", resourcePlaceholder: "例如 satyanadella" },
  { id: "github.trending_repos", platform: "github", label: "热门仓库", description: "读取 GitHub Trending 公开仓库榜单", kind: "trending", price: 1 },
  { id: "google.search", platform: "google", label: "网页搜索", description: "使用隔离的无痕浏览器搜索 Google 公开网页", kind: "search", price: 1 },
  { id: "google.news", platform: "google", label: "新闻搜索", description: "搜索 Google News RSS 公开新闻", kind: "search", price: 1 },
  { id: "google.suggest", platform: "google", label: "搜索联想", description: "读取 Google 匿名搜索建议", kind: "search", price: 1 },
  { id: "google.trends", platform: "google", label: "每日趋势", description: "按国家读取 Google Trends 每日趋势", kind: "trending", price: 1 },
  { id: "imdb.search", platform: "imdb", label: "搜索影视", description: "搜索 IMDb 影视条目和公众影人", kind: "search", price: 1 },
  { id: "imdb.title", platform: "imdb", label: "影视详情", description: "按 IMDb tt ID 读取影视详情", kind: "detail", price: 1, resourceLabel: "Title ID", resourcePlaceholder: "tt1375666" },
  { id: "imdb.top", platform: "imdb", label: "高分榜", description: "读取 IMDb Top Rated Movies", kind: "trending", price: 1 },
  { id: "imdb.trending", platform: "imdb", label: "流行榜", description: "读取 IMDb Most Popular Movies", kind: "trending", price: 1 },
  { id: "imdb.person", platform: "imdb", label: "影人详情", description: "读取演员、导演等公众影人资料", kind: "detail", price: 1, resourceLabel: "Name ID", resourcePlaceholder: "nm0000138" },
  { id: "imdb.reviews", platform: "imdb", label: "影视评论", description: "读取评论正文，不返回作者或账号标识", kind: "comments", price: 1, resourceLabel: "Title ID", resourcePlaceholder: "tt1375666" },
]

const apiKeys = useApiKeysStore()
const session = useSessionStore()
const platform = ref<Platform>("rednote")
const operationId = ref<OperationId>("rednote.search_notes")
const apiKey = ref(apiKeys.revealedKey?.plaintext ?? "")
const phrase = ref("AI Agent")
const resourceId = ref("")
const contentType = ref<"answer" | "article">("answer")
const batchSize = ref(20)
const ranking = ref("relevance")
const freshness = ref("anytime")
const mediaFormat = ref("any")
const proximity = ref("anywhere")
const latitude = ref(39.9042)
const longitude = ref(116.4074)
const localCategoryId = ref("")
const tophubCategory = ref("news")
const tophubPage = ref(1)
const tophubOrder = ref<"hot" | "latest">("hot")
const doubanSubjectType = ref<"movie" | "tv" | "book" | "music" | "drama" | "podcast" | "event" | "game">("movie")
const weiboAuthorId = ref("")
const smzdmTab = ref<"all" | "deals" | "wiki" | "community" | "showcase">("all")
const smzdmOrder = ref<"hot" | "score">("score")
const toutiaoCategory = ref<"__all__" | "news_tech" | "news_finance" | "news_world" | "news_sports" | "news_entertainment" | "news_military">("__all__")
const toutiaoPage = ref(0)
const sinafinancePage = ref(1)
const sinafinanceMarket = ref<"cn" | "hk" | "us" | "wh" | "ft">("cn")
const juejinCategory = ref<"backend" | "frontend" | "android" | "ios" | "ai">("backend")
const kr36HotType = ref<"renqi" | "zonghe" | "shoucang">("renqi")
interface CtripCityOption {
  city_id: number
  district_id: number
  name: string
  label: string
  country: string
  name_en: string
}
const ctripCityPhrase = ref("")
const ctripCityOptions = ref<CtripCityOption[]>([])
const ctripCityId = ref("")
const ctripCityLoading = ref(false)
const ctripCityError = ref("")
const ctripCitySearched = ref(false)
let ctripCityRequest = 0
watch([ctripCityPhrase, apiKey], () => {
  ctripCityRequest++
  ctripCityOptions.value = []
  ctripCityId.value = ""
  ctripCityLoading.value = false
  ctripCityError.value = ""
  ctripCitySearched.value = false
})
async function searchCtripCities(): Promise<void> {
  const request = ++ctripCityRequest
  ctripCityLoading.value = true
  ctripCityId.value = ""
  ctripCityOptions.value = []
  ctripCityError.value = ""
  ctripCitySearched.value = false
  try {
    const response = await apiRequest<{ items: CtripCityOption[] }>(
      `/v1/ctrip/cities?${new URLSearchParams({ phrase: ctripCityPhrase.value.trim() })}`,
      { apiKey: apiKey.value },
    )
    if (request !== ctripCityRequest) return
    ctripCityOptions.value = response.items
    ctripCitySearched.value = true
    if (response.items.length === 1) ctripCityId.value = String(response.items[0]!.city_id)
  } catch (cause) {
    if (request !== ctripCityRequest) return
    ctripCityError.value = cause instanceof ApiError ? cause.message : "城市查询失败，请重试。"
  } finally {
    if (request === ctripCityRequest) ctripCityLoading.value = false
  }
}
const ctripCheckIn = ref(localDateAfter(1))
const ctripCheckOut = ref(localDateAfter(2))
const ctripDeparture = ref("北京")
const ctripArrival = ref("上海")
const ctripFlightCities = [
  { value: "BJS", label: "北京" }, { value: "SHA", label: "上海" },
  { value: "CAN", label: "广州" }, { value: "SZX", label: "深圳" },
  { value: "CTU", label: "成都" }, { value: "HGH", label: "杭州" },
  { value: "XIY", label: "西安" }, { value: "CKG", label: "重庆" },
  { value: "NKG", label: "南京" }, { value: "WUH", label: "武汉" },
  { value: "CSX", label: "长沙" }, { value: "XMN", label: "厦门" },
  { value: "TAO", label: "青岛" }, { value: "TSN", label: "天津" },
  { value: "SYX", label: "三亚" },
]
const ctripFlightDeparture = ref("BJS")
const ctripFlightArrival = ref("SHA")
function localDateAfter(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}
const ctripDepartureDate = ref(localDateAfter(1))
const ctripDestination = ref("北京")
const linkedinLocation = ref("")
const linkedinExperience = ref("")
const linkedinJobType = ref("")
const linkedinDatePosted = ref("")
const linkedinRemote = ref("")
const linkedinStart = ref(0)
const githubSince = ref<"daily" | "weekly" | "monthly">("daily")
const githubLanguage = ref("")
const githubLimit = ref(25)
const googleLanguage = ref("zh-CN")
const googleRegion = ref("US")
const maimaiPostKind = ref<"feed" | "gossip">("feed")
const maimaiEncodedId = ref("")
const recentMaimaiPostRecords = ref<Record<string, unknown>[]>([])
const maimaiPostMetadataNotice = ref("")
const loading = ref(false)
const result = ref<ExecutionResponse | null>(null)
const error = ref("")
const copied = ref(false)

const platformOperations = computed(() => operations.filter((item) => item.platform === platform.value))
const operationOptions = computed(() =>
  platformOperations.value.map((item) => ({
    value: item.id,
    label: `${item.id} · ${item.label}`,
  })),
)
const operation = computed(() => operations.find((item) => item.id === operationId.value) ?? operations[0]!)
const rankingOptions = [
  { value: "relevance", label: "综合相关" },
  { value: "newest", label: "最新发布" },
  { value: "most_liked", label: "最多赞" },
  { value: "most_discussed", label: "最多评论" },
  { value: "most_saved", label: "最多收藏" },
]
const freshnessOptions = [
  { value: "anytime", label: "不限" },
  { value: "today", label: "今天" },
  { value: "week", label: "一周内" },
  { value: "half_year", label: "半年内" },
]
const mediaFormatOptions = [
  { value: "any", label: "不限" },
  { value: "image", label: "图文" },
  { value: "video", label: "视频" },
]
const proximityOptions = [
  { value: "anywhere", label: "不限" },
  { value: "same_city", label: "同城" },
  { value: "nearby", label: "附近" },
]
const contentTypeOptions = [
  { value: "answer", label: "回答" },
  { value: "article", label: "文章" },
]
const tophubCategoryOptions = [
  { value: "news", label: "综合" },
  { value: "tech", label: "科技" },
  { value: "ent", label: "娱乐" },
  { value: "community", label: "社区" },
  { value: "shopping", label: "购物" },
  { value: "finance", label: "财经" },
  { value: "developer", label: "开发" },
  { value: "brief", label: "简报" },
  { value: "ai", label: "AI" },
  { value: "epaper", label: "报刊" },
  { value: "design", label: "设计" },
  { value: "organization", label: "财政/政务" },
  { value: "hot", label: "榜中榜" },
]
const tophubOrderOptions = [
  { value: "hot", label: "热门" },
  { value: "latest", label: "最新" },
]
const doubanSubjectTypeOptions = [
  { value: "movie", label: "电影" }, { value: "tv", label: "剧集" },
  { value: "book", label: "图书" }, { value: "music", label: "音乐" },
  { value: "drama", label: "舞台剧" }, { value: "podcast", label: "播客" },
  { value: "event", label: "同城活动" }, { value: "game", label: "游戏" },
]
const smzdmTabOptions = [
  { value: "all", label: "综合" }, { value: "deals", label: "好价" },
  { value: "wiki", label: "百科" }, { value: "community", label: "值友说" },
  { value: "showcase", label: "晒物" },
]
const smzdmOrderOptions = [
  { value: "score", label: "相关度" }, { value: "hot", label: "热度" },
]
const toutiaoCategoryOptions = [
  { value: "__all__", label: "综合" },
  { value: "news_tech", label: "科技" },
  { value: "news_finance", label: "财经" },
  { value: "news_world", label: "国际" },
  { value: "news_sports", label: "体育" },
  { value: "news_entertainment", label: "娱乐" },
  { value: "news_military", label: "军事" },
]
const sinafinanceMarketOptions = [
  { value: "cn", label: "A 股" },
  { value: "hk", label: "港股" },
  { value: "us", label: "美股" },
  { value: "wh", label: "外汇" },
  { value: "ft", label: "期货 / 商品" },
]
const juejinCategoryOptions = [
  { value: "backend", label: "后端" },
  { value: "frontend", label: "前端" },
  { value: "android", label: "Android" },
  { value: "ios", label: "iOS" },
  { value: "ai", label: "人工智能" },
]
const kr36HotTypeOptions = [
  { value: "renqi", label: "人气" },
  { value: "zonghe", label: "综合" },
  { value: "shoucang", label: "收藏" },
]
const githubSinceOptions = [
  { value: "daily", label: "今日" },
  { value: "weekly", label: "本周" },
  { value: "monthly", label: "本月" },
]
const maimaiPostKindOptions = [
  { value: "feed", label: "普通帖子" },
  { value: "gossip", label: "职言" },
]
const recentMaimaiPostOptions = computed(() =>
  recentMaimaiPostRecords.value.flatMap((record) => {
    const uid = typeof record.uid === "string" || typeof record.uid === "number" ? String(record.uid) : ""
    if (!uid) return []
    const heading = typeof record.heading === "string" ? record.heading.trim() : ""
    const label = heading ? `${uid} · ${heading.slice(0, 42)}` : uid
    return [{ value: uid, label }]
  }),
)
const canRun = computed(() => {
  if (!apiKey.value) return false
  if (operation.value.disabled) return false
  if (operation.value.id === "ctrip.hotels" || operation.value.id === "ctrip.hotel_detail") {
    return ctripCityOptions.value.some((city) => String(city.city_id) === ctripCityId.value)
      && ctripCheckIn.value >= localDateAfter(0)
      && ctripCheckOut.value > ctripCheckIn.value
      && (operation.value.id !== "ctrip.hotel_detail" || Boolean(resourceId.value.trim()))
  }
  if (operation.value.id === "ctrip.flights") {
    return ctripFlightCities.some((city) => city.value === ctripFlightDeparture.value)
      && ctripFlightCities.some((city) => city.value === ctripFlightArrival.value)
      && ctripFlightDeparture.value !== ctripFlightArrival.value
      && ctripDepartureDate.value >= localDateAfter(0)
  }
  if (operation.value.kind === "search") return Boolean(phrase.value.trim())
  if (operation.value.kind === "detail" || operation.value.kind === "comments") {
    if (!resourceId.value.trim()) return false
    if (operation.value.id === "maimai.post" && !phrase.value.trim()) return false
    if (operation.value.id === "weibo.status" || operation.value.id === "weibo.comments") return Boolean(weiboAuthorId.value.trim())
    if ((operation.value.id === "maimai.post" || operation.value.id === "maimai.comments") && maimaiPostKind.value === "gossip") return Boolean(maimaiEncodedId.value.trim())
    return true
  }
  if (operation.value.id === "tophub.tags" || operation.value.kind === "items") return Boolean(tophubCategory.value)
  return true
})

watch(platform, (value) => {
  operationId.value = operations.find((item) => item.platform === value)!.id
  if (value === "tophub") phrase.value = ""
  result.value = null
  error.value = ""
})

watch(tophubCategory, (value) => {
  if (value === "hot") tophubPage.value = 1
  if (["hot", "organization"].includes(value)) tophubOrder.value = "hot"
  if (["hot", "organization", "brief", "ai"].includes(value)) phrase.value = ""
})

watch([operationId, resourceId], syncMaimaiPostMetadata)

function syncMaimaiPostMetadata(): void {
  if (operationId.value !== "maimai.post" && operationId.value !== "maimai.comments") {
    maimaiPostMetadataNotice.value = ""
    return
  }
  const postId = resourceId.value.trim()
  const record = recentMaimaiPostRecords.value.find((item) => String(item.uid ?? "") === postId)
  if (!record) {
    maimaiPostMetadataNotice.value = postId
      ? "未在本页最近搜索结果中找到该 ID，请按搜索结果同时填写 detail_kind；职言还需 encoded_id。"
      : "可从本页最近搜索结果选择帖子，系统会自动填充帖子类型和 Encoded ID。"
    return
  }
  const detailKind = record.detail_kind
  if (detailKind !== "feed" && detailKind !== "gossip") return
  maimaiPostKind.value = detailKind
  maimaiEncodedId.value = detailKind === "gossip" && typeof record.encoded_id === "string"
    ? record.encoded_id
    : ""
  maimaiPostMetadataNotice.value = detailKind === "gossip"
    ? "已从最近搜索结果自动填充：职言与 Encoded ID。"
    : "已从最近搜索结果自动填充：普通帖子。"
}

function requestConfig(): { path: string; method: "GET" | "POST"; body?: unknown } {
  switch (operationId.value) {
    case "rednote.search_notes": return {
      path: "/v1/content/search",
      method: "POST",
      body: {
        channel: "rednote",
        phrase: phrase.value,
        ranking: ranking.value,
        freshness: freshness.value,
        media_format: mediaFormat.value,
        proximity: proximity.value,
      },
    }
    case "rednote.trending": return { path: `/v1/content/trends?channel=rednote&batch_size=${batchSize.value}`, method: "GET" }
    case "rednote.note_detail": return { path: `/v1/content/rednote/post/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "rednote.note_comments": return { path: `/v1/content/rednote/post/${encodeURIComponent(resourceId.value)}/replies?batch_size=${batchSize.value}`, method: "GET" }
    case "rednote.local_feed": {
      const params = new URLSearchParams({
        latitude: String(latitude.value),
        longitude: String(longitude.value),
        batch_size: String(batchSize.value),
      })
      if (localCategoryId.value.trim()) params.set("category_id", localCategoryId.value.trim())
      return { path: `/v1/rednote/local-feed?${params}`, method: "GET" }
    }
    case "rednote.local_feed_header": return { path: `/v1/rednote/local-feed/header?latitude=${latitude.value}&longitude=${longitude.value}`, method: "GET" }
    case "rednote.user_posts": return { path: `/v1/rednote/users/${encodeURIComponent(resourceId.value)}/posts?batch_size=${batchSize.value}`, method: "GET" }
    case "zhihu.search": return { path: "/v1/content/search", method: "POST", body: { channel: "zhihu", phrase: phrase.value, batch_size: batchSize.value } }
    case "zhihu.hot": return { path: `/v1/content/trends?channel=zhihu&batch_size=${batchSize.value}`, method: "GET" }
    case "zhihu.article": return { path: `/v1/content/zhihu/article/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "zhihu.answer": return { path: `/v1/content/zhihu/answer/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "zhihu.comments": return { path: `/v1/content/zhihu/${contentType.value}/${encodeURIComponent(resourceId.value)}/replies?batch_size=${batchSize.value}`, method: "GET" }
    case "tophub.categories": return { path: "/v1/tophub/categories", method: "GET" }
    case "tophub.tags": return { path: `/v1/tophub/${encodeURIComponent(tophubCategory.value)}/tags`, method: "GET" }
    case "tophub.items": {
      const params = new URLSearchParams({
        page: String(tophubPage.value),
        order: tophubOrder.value,
        limit: String(batchSize.value),
      })
      if (
        phrase.value.trim()
        && !["hot", "organization", "brief", "ai"].includes(tophubCategory.value)
      ) {
        params.set("query", phrase.value.trim())
      }
      return {
        path: `/v1/tophub/${encodeURIComponent(tophubCategory.value)}/items?${params}`,
        method: "GET",
      }
    }
    case "douban.search": {
      const params = new URLSearchParams({ phrase: phrase.value, subject_type: doubanSubjectType.value, batch_size: String(Math.min(batchSize.value, 20)) })
      return { path: `/v1/douban/search?${params}`, method: "GET" }
    }
    case "douban.subject": return { path: `/v1/douban/subjects/${doubanSubjectType.value}/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "douban.reviews": return { path: `/v1/douban/subjects/${doubanSubjectType.value}/${encodeURIComponent(resourceId.value)}/reviews?batch_size=${batchSize.value}`, method: "GET" }
    case "douban.comments": return { path: `/v1/douban/subjects/${doubanSubjectType.value}/${encodeURIComponent(resourceId.value)}/comments?batch_size=${batchSize.value}`, method: "GET" }
    case "weibo.search": return { path: `/v1/weibo/search?${new URLSearchParams({ phrase: phrase.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "weibo.hot": return { path: `/v1/weibo/hot?batch_size=${batchSize.value}`, method: "GET" }
    case "weibo.status": return { path: `/v1/weibo/statuses/${encodeURIComponent(resourceId.value)}?author_id=${encodeURIComponent(weiboAuthorId.value)}`, method: "GET" }
    case "weibo.comments": return { path: `/v1/weibo/statuses/${encodeURIComponent(resourceId.value)}/comments?author_id=${encodeURIComponent(weiboAuthorId.value)}&batch_size=${batchSize.value}`, method: "GET" }
    case "weibo_web.search": return { path: `/v1/weibo-web/search?${new URLSearchParams({ phrase: phrase.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "weibo_web.hot": return { path: `/v1/weibo-web/hot?batch_size=${batchSize.value}`, method: "GET" }
    case "weibo_web.status": return { path: `/v1/weibo-web/statuses/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "weibo_web.comments": return { path: `/v1/weibo-web/statuses/${encodeURIComponent(resourceId.value)}/comments?batch_size=${batchSize.value}`, method: "GET" }
    case "toutiao.hot": return { path: `/v1/toutiao/hot?batch_size=${batchSize.value}`, method: "GET" }
    case "toutiao.recommend": return { path: `/v1/toutiao/recommend?${new URLSearchParams({ category: toutiaoCategory.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "toutiao.search": return { path: `/v1/toutiao/search?${new URLSearchParams({ phrase: phrase.value, page: String(toutiaoPage.value), batch_size: String(batchSize.value) })}`, method: "GET" }
    case "toutiao.detail": return { path: `/v1/toutiao/contents/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "sinafinance.live_news": return { path: `/v1/sinafinance/live-news?${new URLSearchParams({ page: String(sinafinancePage.value), batch_size: String(batchSize.value), tag: "0" })}`, method: "GET" }
    case "sinafinance.rolling_news": return { path: `/v1/sinafinance/rolling-news?${new URLSearchParams({ page: String(sinafinancePage.value), batch_size: String(batchSize.value) })}`, method: "GET" }
    case "sinafinance.search": return { path: `/v1/sinafinance/search?${new URLSearchParams({ phrase: phrase.value, page: String(sinafinancePage.value), batch_size: String(batchSize.value) })}`, method: "GET" }
    case "sinafinance.detail": return { path: `/v1/sinafinance/contents/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "sinafinance.quote": return { path: `/v1/sinafinance/quote?${new URLSearchParams({ symbols: resourceId.value })}`, method: "GET" }
    case "sinafinance.stock_rank": return { path: `/v1/sinafinance/stock-rank?${new URLSearchParams({ market: sinafinanceMarket.value, page: String(sinafinancePage.value), batch_size: String(batchSize.value) })}`, method: "GET" }
    case "juejin.search": return { path: `/v1/juejin/search?${new URLSearchParams({ phrase: phrase.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "juejin.detail": return { path: `/v1/juejin/articles/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "juejin.hot": return { path: `/v1/juejin/hot?${new URLSearchParams({ category: juejinCategory.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "juejin.recommend": return { path: `/v1/juejin/recommend?${new URLSearchParams({ batch_size: String(batchSize.value) })}`, method: "GET" }
    case "36kr.news": return { path: `/v1/36kr/news?batch_size=${batchSize.value}`, method: "GET" }
    case "36kr.search": return { path: `/v1/36kr/search?${new URLSearchParams({ phrase: phrase.value, sort: "score", batch_size: String(batchSize.value) })}`, method: "GET" }
    case "36kr.article": return { path: `/v1/36kr/articles/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "36kr.hot": return { path: `/v1/36kr/hot?${new URLSearchParams({ type: kr36HotType.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "dongchedi.search": return { path: `/v1/dongchedi/search?${new URLSearchParams({ phrase: phrase.value, batch_size: String(Math.min(batchSize.value, 30)) })}`, method: "GET" }
    case "dongchedi.series": return { path: `/v1/dongchedi/series/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "dongchedi.models": return { path: `/v1/dongchedi/series/${encodeURIComponent(resourceId.value)}/models?status=online`, method: "GET" }
    case "dongchedi.specs": return { path: `/v1/dongchedi/series/${encodeURIComponent(resourceId.value)}/specs`, method: "GET" }
    case "dongchedi.score": return { path: `/v1/dongchedi/series/${encodeURIComponent(resourceId.value)}/score`, method: "GET" }
    case "dongchedi.reviews": return { path: `/v1/dongchedi/series/${encodeURIComponent(resourceId.value)}/reviews?batch_size=${Math.min(batchSize.value, 15)}`, method: "GET" }
    case "ctrip.search": return { path: `/v1/ctrip/search?${new URLSearchParams({ phrase: phrase.value, batch_size: String(Math.min(batchSize.value, 30)) })}`, method: "GET" }
    case "ctrip.hotel_suggest": return { path: `/v1/ctrip/hotel-suggest?${new URLSearchParams({ phrase: phrase.value, batch_size: String(Math.min(batchSize.value, 30)) })}`, method: "GET" }
    case "ctrip.hotels": return { path: `/v1/ctrip/hotels?${new URLSearchParams({ city_id: String(ctripCityId.value), check_in: ctripCheckIn.value, check_out: ctripCheckOut.value, page: "1", batch_size: String(Math.min(batchSize.value, 20)) })}`, method: "GET" }
    case "ctrip.hotel_detail": return { path: `/v1/ctrip/hotels/${encodeURIComponent(resourceId.value)}?${new URLSearchParams({ city_id: String(ctripCityId.value), check_in: ctripCheckIn.value, check_out: ctripCheckOut.value })}`, method: "GET" }
    case "ctrip.trains": return { path: `/v1/ctrip/trains?${new URLSearchParams({ departure: ctripDeparture.value, arrival: ctripArrival.value, departure_date: ctripDepartureDate.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "ctrip.flights": return { path: `/v1/ctrip/flights?${new URLSearchParams({ departure: ctripFlightDeparture.value, arrival: ctripFlightArrival.value, departure_date: ctripDepartureDate.value })}`, method: "GET" }
    case "ctrip.attractions": return { path: `/v1/ctrip/attractions?${new URLSearchParams({ destination: ctripDestination.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "linkedin.search": return { path: `/v1/linkedin/jobs/search?${new URLSearchParams({ phrase: phrase.value, location: linkedinLocation.value, experience: linkedinExperience.value, job_type: linkedinJobType.value, date_posted: linkedinDatePosted.value, remote: linkedinRemote.value, start: String(linkedinStart.value), batch_size: String(Math.min(batchSize.value, 100)) })}`, method: "GET" }
    case "linkedin.job_detail": return { path: `/v1/linkedin/jobs/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "linkedin.profile": return { path: `/v1/linkedin/profiles/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "linkedin.company": return { path: `/v1/linkedin/companies/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "linkedin.posts": return { path: `/v1/linkedin/profiles/${encodeURIComponent(resourceId.value)}/posts?batch_size=${Math.min(batchSize.value, 100)}`, method: "GET" }
    case "github.trending_repos": return { path: `/v1/github/trending?${new URLSearchParams({ since: githubSince.value, language: githubLanguage.value.trim(), limit: String(githubLimit.value) })}`, method: "GET" }
    case "google.search": return { path: `/v1/google/search?${new URLSearchParams({ phrase: phrase.value, lang: googleLanguage.value, limit: String(Math.min(batchSize.value, 100)) })}`, method: "GET" }
    case "google.news": return { path: `/v1/google/news?${new URLSearchParams({ phrase: phrase.value, lang: googleLanguage.value, region: googleRegion.value, limit: String(Math.min(batchSize.value, 100)) })}`, method: "GET" }
    case "google.suggest": return { path: `/v1/google/suggest?${new URLSearchParams({ phrase: phrase.value, lang: googleLanguage.value })}`, method: "GET" }
    case "google.trends": return { path: `/v1/google/trends?${new URLSearchParams({ region: googleRegion.value, limit: String(Math.min(batchSize.value, 100)) })}`, method: "GET" }
    case "imdb.search": return { path: `/v1/imdb/search?${new URLSearchParams({ phrase: phrase.value, limit: String(batchSize.value) })}`, method: "GET" }
    case "imdb.title": return { path: `/v1/imdb/titles/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "imdb.top": return { path: `/v1/imdb/top?limit=${Math.min(batchSize.value, 100)}`, method: "GET" }
    case "imdb.trending": return { path: `/v1/imdb/trending?limit=${Math.min(batchSize.value, 100)}`, method: "GET" }
    case "imdb.person": return { path: `/v1/imdb/people/${encodeURIComponent(resourceId.value)}?limit=${Math.min(batchSize.value, 50)}`, method: "GET" }
    case "imdb.reviews": return { path: `/v1/imdb/titles/${encodeURIComponent(resourceId.value)}/reviews?limit=${Math.min(batchSize.value, 25)}`, method: "GET" }
    case "smzdm.search": return { path: `/v1/smzdm/search?${new URLSearchParams({ phrase: phrase.value, tab: smzdmTab.value, order: smzdmOrder.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "smzdm.hot": return { path: `/v1/smzdm/hot?batch_size=${batchSize.value}`, method: "GET" }
    case "smzdm.content": return { path: `/v1/smzdm/content/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "smzdm.detail": return { path: `/v1/smzdm/content/${encodeURIComponent(resourceId.value)}/detail`, method: "GET" }
    case "smzdm.comments": return { path: `/v1/smzdm/content/${encodeURIComponent(resourceId.value)}/comments?batch_size=${batchSize.value}`, method: "GET" }
    case "bilibili.search": return { path: `/v1/bilibili/search?${new URLSearchParams({ phrase: phrase.value, batch_size: String(Math.min(batchSize.value, 20)) })}`, method: "GET" }
    case "bilibili.video": return { path: `/v1/bilibili/videos/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "bilibili.hot": return { path: `/v1/bilibili/hot?batch_size=${Math.min(batchSize.value, 20)}`, method: "GET" }
    case "bilibili.rank": return { path: `/v1/bilibili/rank?batch_size=${Math.min(batchSize.value, 20)}`, method: "GET" }
    case "bilibili.comments": return { path: `/v1/bilibili/videos/${encodeURIComponent(resourceId.value)}/comments?batch_size=${Math.min(batchSize.value, 20)}`, method: "GET" }
    case "douyin.search": return { path: `/v1/douyin/search?${new URLSearchParams({ phrase: phrase.value, batch_size: String(Math.min(batchSize.value, 20)) })}`, method: "GET" }
    case "douyin.detail": return { path: `/v1/douyin/awemes/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "douyin.comments": return { path: `/v1/douyin/awemes/${encodeURIComponent(resourceId.value)}/comments?batch_size=${Math.min(batchSize.value, 20)}`, method: "GET" }
    case "douyin.hot": return { path: `/v1/douyin/hot?batch_size=${Math.min(batchSize.value, 20)}`, method: "GET" }
    case "douyin.user_posts": return { path: `/v1/douyin/users/${encodeURIComponent(resourceId.value)}/posts?batch_size=${Math.min(batchSize.value, 20)}`, method: "GET" }
    case "v2ex.hot": return { path: `/v1/v2ex/hot?batch_size=${batchSize.value}`, method: "GET" }
    case "v2ex.latest": return { path: `/v1/v2ex/latest?batch_size=${batchSize.value}`, method: "GET" }
    case "v2ex.node_topics": return { path: `/v1/v2ex/nodes/${encodeURIComponent(resourceId.value)}/topics?batch_size=${batchSize.value}`, method: "GET" }
    case "v2ex.topic": return { path: `/v1/v2ex/topics/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "v2ex.replies": return { path: `/v1/v2ex/topics/${encodeURIComponent(resourceId.value)}/replies?batch_size=${batchSize.value}`, method: "GET" }
    case "xueqiu.search_stocks": return { path: `/v1/xueqiu/search/stocks?${new URLSearchParams({ phrase: phrase.value, batch_size: String(Math.min(batchSize.value, 20)) })}`, method: "GET" }
    case "xueqiu.quote": return { path: `/v1/xueqiu/stocks/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "xueqiu.hot_posts": return { path: `/v1/xueqiu/hot/posts?batch_size=${Math.min(batchSize.value, 20)}`, method: "GET" }
    case "xueqiu.hot_stocks": return { path: `/v1/xueqiu/hot/stocks?batch_size=${Math.min(batchSize.value, 20)}`, method: "GET" }
    case "reddit.search": return { path: `/v1/reddit/search?${new URLSearchParams({ phrase: phrase.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "reddit.popular": return { path: `/v1/reddit/popular?batch_size=${batchSize.value}`, method: "GET" }
    case "reddit.subreddit": return { path: `/v1/reddit/r/${encodeURIComponent(resourceId.value)}?batch_size=${batchSize.value}`, method: "GET" }
    case "reddit.detail": return { path: `/v1/reddit/posts/${encodeURIComponent(resourceId.value)}?batch_size=${batchSize.value}`, method: "GET" }
    case "twitter.search": return { path: `/v1/twitter/search?${new URLSearchParams({ phrase: phrase.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "twitter.tweet": return { path: `/v1/twitter/tweets/${encodeURIComponent(resourceId.value)}?batch_size=${batchSize.value}`, method: "GET" }
    case "twitter.article": return { path: `/v1/twitter/articles/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "jd.search": return { path: `/v1/jd/search?${new URLSearchParams({ phrase: phrase.value, page: "1", batch_size: String(Math.min(batchSize.value, 30)) })}`, method: "GET" }
    case "jd.item": return { path: `/v1/jd/items/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "xianyu.search": return { path: `/v1/xianyu/search?${new URLSearchParams({ phrase: phrase.value, page: "1", batch_size: String(Math.min(batchSize.value, 60)) })}`, method: "GET" }
    case "xianyu.item": return { path: `/v1/xianyu/items/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "1688.item": return { path: `/v1/1688/items/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "1688.store_summary": return { path: `/v1/1688/stores/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "nowcoder.search": return { path: `/v1/nowcoder/search?${new URLSearchParams({ phrase: phrase.value, content_type: "post", page: "1", batch_size: String(batchSize.value) })}`, method: "GET" }
    case "nowcoder.detail": return { path: `/v1/nowcoder/contents/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "nowcoder.papers": return { path: `/v1/nowcoder/papers?${new URLSearchParams({ job_id: "11200", page: "1", batch_size: String(batchSize.value) })}`, method: "GET" }
    case "nowcoder.experience": return { path: `/v1/nowcoder/experience?${new URLSearchParams({ page: "1", batch_size: String(batchSize.value) })}`, method: "GET" }
    case "instagram.search_users": return { path: `/v1/instagram/users/search?${new URLSearchParams({ phrase: phrase.value, batch_size: String(batchSize.value) })}`, method: "GET" }
    case "instagram.profile": return { path: `/v1/instagram/profiles/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "instagram.user_posts": return { path: `/v1/instagram/profiles/${encodeURIComponent(resourceId.value)}/posts?batch_size=${Math.max(12, batchSize.value)}`, method: "GET" }
    case "instagram.post": return { path: `/v1/instagram/posts/${encodeURIComponent(resourceId.value)}`, method: "GET" }
    case "maimai.search_posts": return { path: `/v1/maimai/search/posts?${new URLSearchParams({ phrase: phrase.value, page: "0", batch_size: String(batchSize.value) })}`, method: "GET" }
    case "maimai.search_jobs": return { path: `/v1/maimai/search/jobs?${new URLSearchParams({ phrase: phrase.value, page: "0", batch_size: String(batchSize.value) })}`, method: "GET" }
    case "maimai.search_companies": return { path: `/v1/maimai/search/companies?${new URLSearchParams({ phrase: phrase.value, page: "0", batch_size: String(batchSize.value) })}`, method: "GET" }
    case "maimai.search_talents": return { path: `/v1/maimai/search/talents?${new URLSearchParams({ phrase: phrase.value, page: "0", batch_size: String(batchSize.value) })}`, method: "GET" }
    case "maimai.post": {
      const params = new URLSearchParams({ kind: maimaiPostKind.value })
      if (maimaiEncodedId.value.trim()) params.set("encoded_id", maimaiEncodedId.value.trim())
      params.set("phrase", phrase.value.trim())
      return { path: `/v1/maimai/posts/${encodeURIComponent(resourceId.value)}?${params}`, method: "GET" }
    }
    case "maimai.comments": {
      const params = new URLSearchParams({ kind: maimaiPostKind.value, page: "0", batch_size: String(batchSize.value) })
      if (maimaiEncodedId.value.trim()) params.set("encoded_id", maimaiEncodedId.value.trim())
      return { path: `/v1/maimai/posts/${encodeURIComponent(resourceId.value)}/comments?${params}`, method: "GET" }
    }
    case "jianyu.search": return { path: `/v1/jianyu/search?${new URLSearchParams({ phrase: phrase.value, page: "1", batch_size: String(batchSize.value), since_days: "365", search_scope: "title,content" })}`, method: "GET" }
    case "jianyu.detail": return { path: `/v1/jianyu/notices/${encodeURIComponent(resourceId.value)}`, method: "GET" }
  }
}

async function run(): Promise<void> {
  error.value = ""
  result.value = null
  loading.value = true
  try {
    const requestedOperation = operationId.value
    const config = requestConfig()
    const response = await apiRequest<ExecutionResponse>(config.path, {
      method: config.method,
      apiKey: apiKey.value,
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: config.body,
    })
    result.value = response
    if (requestedOperation === "maimai.search_posts") {
      recentMaimaiPostRecords.value = response.records
      syncMaimaiPostMetadata()
    }
    try {
      await session.refresh()
    } catch {
      // The provider response is authoritative. A secondary session refresh
      // must not replace a successful call result with a generic UI error.
    }
  } catch (caught) {
    if (caught instanceof ApiError && (caught.code === "PROVIDER_UNAVAILABLE" || caught.code === "CAPABILITY_UNAVAILABLE")) {
      error.value = "当前服务暂时不可用，请稍后重试。"
    } else {
      error.value = caught instanceof ApiError ? caught.message : "调用失败，请检查服务状态。"
    }
  } finally {
    loading.value = false
  }
}

async function copyResult(): Promise<void> {
  if (!result.value) return
  await navigator.clipboard.writeText(JSON.stringify(result.value, null, 2))
  copied.value = true
  window.setTimeout(() => (copied.value = false), 1600)
}
</script>

<template>
  <section data-screen-label="Playground">
    <div class="page-heading"><span class="eyebrow">Live request</span><h1>Playground</h1><p>调用公开内容、详情、评论与热榜能力。用户画像和个性化 Feed 不对外开放。</p></div>

    <div class="playground-shell">
      <form class="playground-form" @submit.prevent="run">
        <fieldset class="platform-choice"><legend>平台</legend><label v-for="item in platformOptions" :key="item.id" :class="{ selected: platform === item.id }"><input v-model="platform" type="radio" :value="item.id" /><span>{{ item.label }}</span><small>公开内容搜索与查看</small></label></fieldset>
        <label class="field"><span>Operation</span><AppSelect v-model="operationId" aria-label="Operation" :options="operationOptions" /><small>{{ operation.description }}</small></label>
        <label class="field"><span>API Key</span><span class="input-wrap"><KeyRound :size="16" /><input v-model="apiKey" type="password" required autocomplete="off" placeholder="byre_key_…" /></span><small v-if="!apiKeys.revealedKey">完整 Key 不会写入浏览器存储，刷新后需要重新粘贴。</small></label>
        <label v-if="operation.kind === 'search'" class="field"><span>搜索短语</span><input v-model="phrase" required maxlength="200" /></label>
        <label v-if="operation.id === 'maimai.post'" class="field"><span>原搜索短语</span><input v-model="phrase" required maxlength="200" placeholder="用于重新定位同一条帖子" /><small>请保持为找到该帖时使用的搜索短语。</small></label>
        <label v-if="platform === 'douban'" class="field"><span>豆瓣条目类型</span><AppSelect v-model="doubanSubjectType" aria-label="豆瓣条目类型" :options="doubanSubjectTypeOptions" /></label>
        <label v-if="operation.id === 'weibo.status' || operation.id === 'weibo.comments'" class="field"><span>作者 UID</span><input v-model="weiboAuthorId" required maxlength="40" pattern="[0-9]+" placeholder="搜索结果 creator.uid" /></label>
        <label v-if="operation.id === 'smzdm.search'" class="field"><span>搜索范围</span><AppSelect v-model="smzdmTab" aria-label="什么值得买搜索范围" :options="smzdmTabOptions" /></label>
        <label v-if="operation.id === 'smzdm.search'" class="field"><span>搜索排序</span><AppSelect v-model="smzdmOrder" aria-label="什么值得买搜索排序" :options="smzdmOrderOptions" /></label>
        <label v-if="operation.id === 'toutiao.recommend'" class="field"><span>推荐频道</span><AppSelect v-model="toutiaoCategory" aria-label="今日头条推荐频道" :options="toutiaoCategoryOptions" /></label>
        <label v-if="operation.id === 'toutiao.search'" class="field field--compact"><span>页码</span><input v-model.number="toutiaoPage" type="number" min="0" max="100" /></label>
        <label v-if="platform === 'sinafinance' && operation.id !== 'sinafinance.detail' && operation.id !== 'sinafinance.quote'" class="field field--compact"><span>页码</span><input v-model.number="sinafinancePage" type="number" min="1" max="100" /></label>
        <label v-if="operation.id === 'sinafinance.stock_rank'" class="field"><span>市场</span><AppSelect v-model="sinafinanceMarket" aria-label="新浪财经市场" :options="sinafinanceMarketOptions" /></label>
        <label v-if="operation.id === 'juejin.hot'" class="field"><span>文章分类</span><AppSelect v-model="juejinCategory" aria-label="掘金文章分类" :options="juejinCategoryOptions" /></label>
        <label v-if="operation.id === '36kr.hot'" class="field"><span>榜单类型</span><AppSelect v-model="kr36HotType" aria-label="36氪榜单类型" :options="kr36HotTypeOptions" /></label>
        <template v-if="operation.id === 'ctrip.hotels' || operation.id === 'ctrip.hotel_detail'">
          <div class="field">
            <label for="ctrip-city-search">目的地城市</label>
            <input id="ctrip-city-search" v-model="ctripCityPhrase" maxlength="80" placeholder="输入中文城市名，如盐城、东京" @keydown.enter.prevent="!ctripCityLoading && apiKey.trim() && ctripCityPhrase.trim() && searchCtripCities()" />
            <button type="button" class="button button--secondary" :disabled="ctripCityLoading || !apiKey.trim() || !ctripCityPhrase.trim()" @click="searchCtripCities">{{ ctripCityLoading ? '查询中…' : '查询城市' }}</button>
            <small>免费查询携程目的地。请填写城市名，选择匹配结果后搜索酒店。</small>
            <span v-if="ctripCityError" role="alert">{{ ctripCityError }}</span>
            <small v-else-if="ctripCitySearched && !ctripCityOptions.length" role="status">未找到匹配城市，请补充或更换城市名称。</small>
            <fieldset v-if="ctripCityOptions.length" class="ctrip-city-results">
              <legend>匹配城市</legend>
              <label v-for="city in ctripCityOptions" :key="city.district_id">
                <input v-model="ctripCityId" type="radio" name="ctrip-city" :value="String(city.city_id)" />
                {{ city.label }} · {{ city.country }}
              </label>
            </fieldset>
          </div>
          <label class="field"><span>入住日期</span><input v-model="ctripCheckIn" type="date" :min="localDateAfter(0)" required /></label>
          <label class="field"><span>离店日期</span><input v-model="ctripCheckOut" type="date" :min="ctripCheckIn" required /></label>
          <small v-if="ctripCheckOut <= ctripCheckIn">离店日期必须晚于入住日期。</small>

        </template>
        <template v-if="operation.id === 'ctrip.trains' || operation.id === 'ctrip.flights'">
          <template v-if="operation.id === 'ctrip.flights'">
            <label class="field"><span>出发地</span><AppSelect v-model="ctripFlightDeparture" aria-label="机票出发地" :options="ctripFlightCities" /></label>
            <label class="field"><span>到达地</span><AppSelect v-model="ctripFlightArrival" aria-label="机票到达地" :options="ctripFlightCities" /></label>
          </template>
          <template v-else>
            <label class="field"><span>出发地</span><input v-model="ctripDeparture" required maxlength="80" placeholder="北京" /></label>
            <label class="field"><span>到达地</span><input v-model="ctripArrival" required maxlength="80" placeholder="上海" /></label>
          </template>
          <label class="field"><span>出发日期</span><input v-model="ctripDepartureDate" type="date" :min="localDateAfter(0)" required /></label>
          <small v-if="operation.id === 'ctrip.flights' && ctripFlightDeparture === ctripFlightArrival">出发地与到达地不能相同。</small>
        </template>
        <label v-if="operation.id === 'ctrip.attractions'" class="field"><span>目的地</span><input v-model="ctripDestination" maxlength="80" placeholder="例如：北京、杭州、上海" required /><small>系统会自动解析携程内部区域 ID，无需手工查找。</small></label>
        <template v-if="operation.id === 'linkedin.search'">
          <label class="field"><span>地点（可选）</span><input v-model="linkedinLocation" maxlength="160" placeholder="例如 Shanghai、Singapore" /></label>
          <label class="field field--compact"><span>起始位置</span><input v-model.number="linkedinStart" type="number" min="0" max="1000" /></label>
          <label class="field"><span>经验级别（可选）</span><input v-model="linkedinExperience" maxlength="80" placeholder="例如 entry,senior" /></label>
          <label class="field"><span>职位类型（可选）</span><input v-model="linkedinJobType" maxlength="80" placeholder="例如 full-time,contract" /></label>
          <label class="field"><span>发布时间（可选）</span><input v-model="linkedinDatePosted" maxlength="40" placeholder="例如 day、week、month" /></label>
          <label class="field"><span>办公方式（可选）</span><input v-model="linkedinRemote" maxlength="40" placeholder="例如 remote、hybrid、onsite" /></label>
        </template>
        <template v-if="operation.id === 'github.trending_repos'">
          <label class="field"><span>时间范围</span><AppSelect v-model="githubSince" aria-label="GitHub Trending 时间范围" :options="githubSinceOptions" /></label>
          <label class="field"><span>编程语言（可选）</span><input v-model="githubLanguage" maxlength="80" pattern="[A-Za-z0-9+#._-]*" placeholder="例如 python、rust、c++" /></label>
          <label class="field field--compact"><span>返回数量</span><input v-model.number="githubLimit" type="number" min="1" max="25" /></label>
        </template>
        <template v-if="platform === 'google'">
          <label v-if="operation.id !== 'google.trends'" class="field"><span>语言</span><input v-model="googleLanguage" maxlength="8" pattern="[A-Za-z]{2,3}(-[A-Za-z]{2,4})?" placeholder="zh-CN" required /></label>
          <label v-if="operation.id === 'google.news' || operation.id === 'google.trends'" class="field"><span>国家/地区代码</span><input v-model="googleRegion" maxlength="2" pattern="[A-Za-z]{2}" placeholder="US" required /></label>
        </template>
        <label v-if="operation.id === 'tophub.tags' || operation.kind === 'items'" class="field"><span>TopHub 分类</span><AppSelect v-model="tophubCategory" aria-label="TopHub 分类" :options="tophubCategoryOptions" /></label>
        <label v-if="operation.kind === 'items'" class="field"><span>导航关键词（可选）</span><input v-model="phrase" maxlength="80" placeholder="例如：新浪、出海" /></label>
        <label v-if="operation.kind === 'items'" class="field field--compact"><span>页码</span><input v-model.number="tophubPage" type="number" min="1" max="200" /></label>
        <label v-if="operation.kind === 'items' && tophubCategory !== 'hot' && tophubCategory !== 'organization'" class="field"><span>排序</span><AppSelect v-model="tophubOrder" aria-label="TopHub 排序" :options="tophubOrderOptions" /></label>
        <template v-if="operation.id === 'rednote.search_notes'">
          <label class="field"><span>排序</span><AppSelect v-model="ranking" aria-label="排序" :options="rankingOptions" /></label>
          <label class="field"><span>发布时间</span><AppSelect v-model="freshness" aria-label="发布时间" :options="freshnessOptions" /></label>
          <label class="field"><span>媒体形式</span><AppSelect v-model="mediaFormat" aria-label="媒体形式" :options="mediaFormatOptions" /></label>
          <label class="field"><span>距离</span><AppSelect v-model="proximity" aria-label="距离" :options="proximityOptions" /></label>
          <small>RedNote 搜索每页固定返回最多 20 条。</small>
        </template>
        <template v-if="operation.id === 'rednote.local_feed' || operation.id === 'rednote.local_feed_header'">
          <label class="field field--compact"><span>纬度</span><input v-model.number="latitude" type="number" min="-90" max="90" step="0.000001" required /></label>
          <label class="field field--compact"><span>经度</span><input v-model.number="longitude" type="number" min="-180" max="180" step="0.000001" required /></label>
          <label v-if="operation.id === 'rednote.local_feed'" class="field"><span>城市子频道 ID（可选）</span><input v-model="localCategoryId" maxlength="100" pattern="[A-Za-z0-9._-]+" placeholder="例如 local.photo（已知频道 ID 时填写）" /></label>
        </template>
        <label v-if="(operation.id === 'maimai.post' || operation.id === 'maimai.comments') && recentMaimaiPostOptions.length" class="field"><span>从最近搜索结果选择</span><AppSelect v-model="resourceId" aria-label="最近搜索到的脉脉帖子" :options="recentMaimaiPostOptions" placeholder="选择后自动填充帖子类型" /></label>
        <label v-if="operation.resourceLabel" class="field"><span>{{ operation.resourceLabel }}</span><input v-model="resourceId" required :maxlength="operation.id === 'sinafinance.detail' ? 1024 : 320" :placeholder="operation.resourcePlaceholder" :pattern="operation.id === 'instagram.profile' || operation.id === 'instagram.user_posts' ? '[A-Za-z0-9._]+' : operation.id === 'sinafinance.quote' ? '[A-Za-z0-9,_.\-]+' : '[A-Za-z0-9_\-]+'" /><small v-if="operation.id === 'maimai.post' || operation.id === 'maimai.comments'">{{ maimaiPostMetadataNotice }}</small></label>
        <label v-if="operation.id === 'maimai.post' || operation.id === 'maimai.comments'" class="field"><span>帖子类型</span><AppSelect v-model="maimaiPostKind" aria-label="脉脉帖子类型" :options="maimaiPostKindOptions" /></label>
        <label v-if="(operation.id === 'maimai.post' || operation.id === 'maimai.comments') && maimaiPostKind === 'gossip'" class="field"><span>职言 Encoded ID</span><input v-model="maimaiEncodedId" required maxlength="200" pattern="[A-Za-z0-9_\-]+" placeholder="搜索结果 encoded_id" /></label>
        <label v-if="operation.id === 'zhihu.comments'" class="field"><span>内容类型</span><AppSelect v-model="contentType" aria-label="内容类型" :options="contentTypeOptions" /></label>
        <label v-if="operation.id !== 'github.trending_repos' && operation.id !== 'google.suggest' && (operation.kind === 'search' || operation.kind === 'comments' || operation.kind === 'trending' || operation.kind === 'items' || operation.id === 'instagram.user_posts')" class="field field--compact"><span>批次数量</span><input v-model.number="batchSize" type="number" :min="operation.id === 'instagram.user_posts' ? 12 : 1" :max="operation.kind === 'items' ? 500 : platform === 'google' ? 100 : operation.id === 'douban.search' || platform === 'bilibili' || platform === 'douyin' ? 20 : 50" /></label>
        <div class="request-cost"><span>本次预估</span><strong>{{ operation.price }} {{ operation.price === 1 ? "credit" : "credits" }}</strong></div>
        <button class="primary-button full-button" :disabled="loading || !canRun"><LoaderCircle v-if="loading" class="spin" :size="17" /><Play v-else :size="16" fill="currentColor" />{{ loading ? "正在调用 Provider" : operation.disabled ? "当前暂不可用" : `运行 ${operation.label}` }}<ArrowRight v-if="!loading && !operation.disabled" :size="16" /></button>
      </form>

      <section class="response-panel" aria-live="polite">
        <header><div><span class="eyebrow">Response</span><strong>{{ result ? "200 OK" : error ? "REQUEST FAILED" : "READY" }}</strong></div><button v-if="result" class="copy-button copy-button--dark" @click="copyResult"><Check v-if="copied" :size="14" /><Clipboard v-else :size="14" />{{ copied ? "已复制" : "复制 JSON" }}</button></header>
        <div v-if="loading" class="terminal-empty"><LoaderCircle class="spin" :size="22" /><span>执行 {{ operation.id }}…</span></div>
        <div v-else-if="error" class="terminal-error"><AlertTriangle :size="22" /><strong>调用没有完成</strong><p>{{ error }}</p><button class="terminal-action" @click="run"><RotateCcw :size="14" />重试</button></div>
        <pre v-else-if="result"><code>{{ JSON.stringify(result, null, 2) }}</code></pre>
        <div v-else class="terminal-empty"><span class="terminal-cursor"></span><span>选择 Operation 并运行请求</span></div>
        <footer v-if="result"><span>trace_id <code>{{ result.trace_id }}</code></span><span>消费 <strong>{{ result.charge.units }}</strong></span><span>余额 <strong>{{ result.charge.balance }}</strong></span></footer>
      </section>
    </div>
  </section>
</template>

<style scoped>

.ctrip-city-results { display: grid; gap: 12px; border: 1px solid #b7c8b7; padding: 16px; }
.ctrip-city-results label { display: flex; align-items: center; gap: 12px; }
.ctrip-city-results input[type="radio"] { width: 20px; height: 20px; min-height: 0; margin: 0; }

</style>
